import { clsx } from 'clsx';
import * as React from "react";
import { lazy, memo } from "react";
import renderItemCustom, { itemClassDefault, renderItemWrapperCustom } from "../../custom/render/renderItemCustom.tsx";
import Dump from "../components/dump";
import { ContentRenderMode, ItemTypeBase, ItemWrapperTypeBase } from "../enums";
import { arrayUnique, decodeHTMLEntities, strip_tags } from "../helpers/utils";
import type { PageRenderedItemData } from "../page/content.types";
import RenderContent from "./renderContent";
import { getBgColorClass, getBgImageClass, getMarginClasses, getPaddingClasses, responsiveHelper } from "./renderFunctions.ts";
import RenderScript from "./renderScript.tsx";

const RenderTabs = lazy(() => import('./renderTabs'));
const RenderSlider = lazy(() => import('./renderSlider'));
const Accordion = lazy(() => import('../../custom/components/accordion.tsx'));
const VimeoIframe = lazy(() => import('../components/vimeoIframe'));

type Props = Readonly<{
	item: PageRenderedItemData
	itemId?: string
	itemLayoutClass?: string[]
	indexSection: number
	indexItem: number
}>

export function renderItemBlockWrapper(item: PageRenderedItemData, rendered: React.ReactNode) {
	if (item?.wrapper) {
		switch (item.wrapper) {
			case ItemWrapperTypeBase.HEADER_BOX:
				return <div className="headerBox">
					<div>{rendered}</div>
				</div>;
		}

		return renderItemWrapperCustom(item.wrapper, rendered);
	}

	return rendered;
}

function RenderItem({item, itemId = null, itemLayoutClass = [], indexSection, indexItem,...otherProps}: Props) {
	let itemClass = [...itemClassDefault];

	if (item.type !== ItemTypeBase.REUSE) { // this stops column overflow, bg colors will be set anyway by the reused content
		//itemStyle = getBgStyle(item);
		itemClass = itemClass.concat(getBgImageClass(item), getBgColorClass(item));
	}

	if (item.type === ItemTypeBase.CONTAINER) {
		itemClass = itemClass.concat(['flex', 'flex-col']);
	}

	if (item?.size) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.size));
	}

	if (item?.order) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.order, 'order-'));
	}

	if (item?.class) {
		itemClass.push(item.class);
	}

	if (item?.textAlign) {
		itemLayoutClass = itemLayoutClass.concat(responsiveHelper(item.textAlign));
	}

	if (item?.textClass) {
		itemLayoutClass.push(item.textClass);
	} else if (item?.textColor) {
		itemLayoutClass.push('text-[' + item.textColor + ']');
	}

	itemLayoutClass = itemLayoutClass.concat(getMarginClasses(item));
	itemClass = itemClass.concat(getPaddingClasses(item));

	function renderItem(item: PageRenderedItemData) {
		switch (item?.type) {
			case ItemTypeBase.CODE:
				return <span dangerouslySetInnerHTML={{__html: decodeHTMLEntities(strip_tags(item.rendered ? item.rendered : item.content.toString()))}}/>

			case ItemTypeBase.TEXT:
				return <span dangerouslySetInnerHTML={{__html: item.rendered ? item.rendered : item.content }}/>

			case ItemTypeBase.TEXT_ANCHOR:
				return <a href={item.href} dangerouslySetInnerHTML={{__html: item.rendered ? item.rendered : item.content}}/>

			case ItemTypeBase.FAQ:
				return <Accordion header={item.title}>
					<span dangerouslySetInnerHTML={{__html: item.rendered ? item.rendered : item.content}}/>
				</Accordion>

			case ItemTypeBase.SCRIPT:
				return <RenderScript src={item.scriptSrc}/>

			case ItemTypeBase.REUSE:
				return <RenderContent content={item?.page?.content} mode={ContentRenderMode.REUSE_BLOCK}/>;

			case ItemTypeBase.CONTAINER:
				if (Array.isArray(item.content)) {
					return item.content.map((containerItem, cIndex) => {
						return <div
							key={"containerItem_" + cIndex}
							className={clsx(
								getPaddingClasses(containerItem).join(' '),
								responsiveHelper(containerItem.marginBottom, 'mb-', true),
								containerItem.class
							)}
						>
							{renderItem(containerItem)}
						</div>
					});
				}

				return <Dump value={item?.content}/>

			case ItemTypeBase.VIMEO:
				return <>{item.vimeoId &&
					<div className="relative pt-[56.25%]">
						<VimeoIframe
							videoId={item.vimeoId}

							autoplay={item.vimeoAutoplay}
							muted={item.vimeoMute}
							loop={item.vimeoLoop}

							showTitle={item.vimeoShowTitle}
							showByline={item.vimeoShowByline}
							showPortrait={item.vimeoShowPortrait}
							showControls={item.vimeoShowControls}

							color={item.vimeoColor}

							className={"vimeoContainer absolute top-0 left-0 w-full h-full"}
						/>
					</div>}
				</>

	        case ItemTypeBase.SLIDER:
	            return <RenderSlider item={item}/>
			case ItemTypeBase.TABS:
				return <RenderTabs item={item}/>
		}

		const toReturn = renderItemCustom(item, indexSection, indexItem);
		if (toReturn !== null) {
			return toReturn;
		}

		return <>
			<Dump value={item?.type} label={"Missing renderer"}/>
			<Dump value={item?.content} label={"Content"}/>
			{item?.rendered && <Dump value={item?.rendered} label="Rendered"/>}
		</>;
	}

	return (<>
		{/*<Dump label="itemLayoutClass" value={itemLayoutClass}/>*/}

		<div id={itemId} className={clsx(arrayUnique(itemLayoutClass))} {...otherProps}>
			<div className={clsx(arrayUnique(itemClass))}>
				{renderItemBlockWrapper(item, renderItem(item))}
			</div>
		</div>
	</>);
}

export default memo(RenderItem)
